import React, { useCallback, useMemo, forwardRef } from 'react';

import { Input } from 'antd';

import currencyFormatter from 'currency-formatter';
import padStart from 'lodash/padStart';
import { cleanCpfAndCnpj, formatCpfAndCnpj } from 'v2/helpers/cpfCnpj';

interface IProps {
  id?: string;
  onChange?: (value: any) => void;
  mask:
    'phone' | 'creditCard' | 'monthYear' | 'cpfCnpj' | 'postalCode' | 'number' | 'money' | 'singledigit' | 'percent';
  value?: string | number;
  label?: string;
  placeHolder?: string;
  margin?: any;
  style?: any;
  startAdornment?: any;
  endAdornment?: any;
  helperText?: string;
  className?: string;
  disabled?: boolean;
  disableUnderline?: boolean;
  defaultValue?: any;
  InputProps?: any;
  InputLabelProps?: any;
  inputProps?: any;
  fullWidth?: any;
  error?: any;
  onBlur?: any;
  name?: any;
  onFocus?: any;
  noStrictMaxPercent?: boolean;
  maskCleanValue?: boolean;
}

const CustomMaskedInput = forwardRef<HTMLDivElement, IProps>((props, ref) => {
  const {
    id,
    onChange,
    mask,
    value: propValue,
    label,
    placeHolder,
    startAdornment,
    endAdornment,
    helperText,
    className,
    disabled,
    defaultValue,
    fullWidth,
    error,
    onBlur,
    name,
    onFocus,
    noStrictMaxPercent,
    maskCleanValue
  } = props;

  const masks = useMemo<
    Record<string, { apply: (value: string) => string; clean: (value: string) => string | number }>
  >(
    () => ({
      phone: {
        apply: (value: string) => {
          if (!value) {
            return value;
          }

          const regexp = value.length > 10 ? /^(\d{0,2})(\d{0,5})(\d{0,4}).*/ : /^(\d{0,2})(\d{0,4})(\d{0,4}).*/;
          const result = value.length > 2 ? '($1) $2-$3' : '($1$2$3';
          return value.replace(regexp, result).replace(/-$/, '');
        },
        clean: (value: string) => value.replace(/\D/gi, '').substr(0, 11)
      },
      creditCard: {
        apply: (value: string) => {
          if (!value) {
            return value;
          }

          const regexp = /^(\d{0,4})?(\d{0,4})?(\d{0,4})?(\d{0,4})?/;
          return value.replace(regexp, '$1 $2 $3 $4').replace(/-$/, '').trim();
        },
        clean: (value: string) => value.replace(/\D/gi, '').substr(0, 16)
      },
      monthYear: {
        apply: (value: string) => {
          if (!value) {
            return value;
          }

          const regexp = /^(\d{2})(\d{0,2})?/;
          return value.replace(/\D/, '').replace(regexp, '$1/$2').replace(/-$/, '').trim();
        },
        clean: (value: string) => value.substr(0, 5)
      },
      cpfCnpj: {
        apply: formatCpfAndCnpj,
        clean: cleanCpfAndCnpj
      },
      postalCode: {
        apply: (value: string) => {
          if (!value) {
            return value;
          }

          const regexp = /^(\d{5})(\d{3})?/;
          return value.replace(/\D/, '').replace(regexp, '$1-$2').replace(/-$/, '').trim();
        },
        clean: (value: string) => value.substr(0, 9)
      },
      number: {
        apply: (value: string) => {
          if (!value) {
            return '';
          }

          return value.replace(/\D/g, '');
        },
        clean: (value: string) => value.replace(/\D/g, '')
      },
      money: {
        apply: (value: string) => {
          if (!value) {
            return '0,00';
          }

          value = value.toString();

          if (value.match(/^\d+$/)) {
            value = `${value}.00`;
          }

          if (value.match(/^\d+\.\d+$/)) {
            value = Number(value).toFixed(2);
          }

          value = value.replace(/\D+/gi, '');
          const intval = value.substr(0, value.length - 2);
          const cents = value.substr(value.length - 2, 2);
          value = `${intval}.${cents}`;
          return currencyFormatter.format(Number(value), { code: 'BRL', precision: 2, symbol: '' });
        },
        clean: (value: string): number => {
          value = (value || '').toString().replace(/[^\d,]/gi, '');

          if (!value.includes(',')) {
            value = '0,' + padStart(value, 2, '0');
          }

          const [, cents] = value.split(',');

          if (cents && cents.length !== 2) {
            value = value
              .replace(',', '')
              .replace(/(\d+)?(\d{2})/gi, '$1,$2')
              .replace(/^,/gi, '0,');
          }

          return parseFloat(value.replace(/\./gi, '').replace(',', '.'));
        }
      },
      singledigit: {
        apply: (value: string) => {
          if (!value) {
            return value;
          }

          return value.replace(/\D/g, '');
        },
        clean: (value: string) => value.substr(0, 1)
      },
      percent: {
        apply: (value: string) => {
          if (!value) {
            return value;
          }

          value = value.toString();

          if (value.match(/^\d+$/)) {
            value = `${value}.00`;
          }

          if (value.match(/^\d+\.\d+$/)) {
            value = Number(value).toFixed(2);
          }

          value = value.replace(/\D+/gi, '');
          const intval = value.substr(0, value.length - 2);
          const cents = value.substr(value.length - 2, 2);
          value = `${intval}.${cents}`;

          if (parseInt(value) > 100 && !noStrictMaxPercent) {
            return '100';
          }

          return currencyFormatter.format(Number(value), { code: 'BRL', precision: 2, symbol: '' }).trim();
        },
        clean: (value: string) => value
      }
    }),
    [noStrictMaxPercent]
  );

  const displayValue = masks[mask].apply((propValue && propValue.toString()) || '');

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const clean = masks[mask].clean(event.target.value);
      const masked = masks[mask].apply(clean.toString());
      onChange(maskCleanValue ? clean : masked);
    },
    [mask, masks, onChange, maskCleanValue]
  );

  return (
    <div ref={ref} className={fullWidth ? 'w-full' : undefined}>
      {label && (
        <label htmlFor={id} className='mb-1 block text-sm'>
          {label}
        </label>
      )}
      <Input
        id={id}
        name={name}
        onFocus={onFocus}
        status={error ? 'error' : undefined}
        value={displayValue}
        disabled={disabled}
        onChange={handleOnChange}
        placeholder={placeHolder}
        className={className}
        defaultValue={defaultValue}
        onBlur={onBlur}
        prefix={startAdornment || undefined}
        suffix={endAdornment || undefined}
      />
      {helperText && (
        <span className={`mt-1 block text-xs ${error ? 'text-red-500' : 'text-gray-500'}`}>{helperText}</span>
      )}
    </div>
  );
});

CustomMaskedInput.displayName = 'CustomMaskedInput';

export default React.memo(CustomMaskedInput);
